import { useTranslation } from "react-i18next";
import type { BillOut } from "../../../../../api/bills";
import { useAppSelector } from "../../../../../store/hooks";
import { customerDiscountOf, formatSignedBillPercent } from "../../billDiscount";

// Лента „закачен клиент" — под хедъра на модала за закриване: бадж с
// процента · име · какво прави процентът · спестената/надбавената сума.
// Показва се САМО при активен discount_applied (клиент с 0% също е клиент).

interface BillCustomerBannerProps {
  bill: Pick<
    BillOut,
    "discount_applied" | "discount_percent" | "total_discount" | "elements"
  >;
  /** Тесен екран — по-дребни кегели и отстъпи. */
  compact?: boolean;
  /** Хоризонталният падинг го дава РОДИТЕЛЯТ (напр. `px-[22px]`), за да
   *  подравни съдържанието с колоните под лентата; тук е само вертикалният. */
  className?: string;
}

export const BillCustomerBanner = ({
  bill,
  compact = false,
  className = "",
}: BillCustomerBannerProps) => {
  const { t } = useTranslation();
  // Продаваемото при склад е рецепта, без склад — асортимент.
  const usesWarehouse = useAppSelector(
    (state) => state.auth.user?.uses_warehouse === true,
  );
  const applied = bill.discount_applied;
  const discount = customerDiscountOf(bill);
  if (!applied || !discount) return null;

  const { percent, isSurcharge, amount } = discount;

  // Клиентският процент важи „на всички редове" само ако никой ред не е с
  // друг процент (отстъпка по ред, „Промяна на цена", заключена цена) —
  // иначе казваме на колко от редовете реално е приложен.
  const items = bill.elements.flatMap((element) => element.items);
  const itemsWithBillPercent = items.filter(
    (item) => (parseFloat(item.percent) || 0) === percent,
  ).length;
  const appliesToAll = itemsWithBillPercent === items.length;

  const kindKey = isSurcharge ? "Surcharge" : "Discount";
  const sellableKey = usesWarehouse ? "Recipes" : "Assortments";
  const description =
    percent === 0
      ? t("clients.bannerNoDiscount")
      : appliesToAll
        ? t(`clients.banner${kindKey}${sellableKey}`, {
            percent: formatSignedBillPercent(percent),
          })
        : t(`clients.banner${kindKey}Partial${sellableKey}`, {
            percent: formatSignedBillPercent(percent),
            count: itemsWithBillPercent,
            total: items.length,
          });

  return (
    <div
      // Лента на цяла ширина: без рамка/радиус, само долна линия към
      // съдържанието под нея — родителят не ѝ дава външни отстояния
      className={`flex items-center border-b border-pos-line2 bg-pos-marked text-pos-ink ${
        compact ? "gap-2.5 py-1" : "gap-3 py-[5px]"
      } ${className}`}
    >
      {/* Водещият елемент е баджът с процента — същите цветове и форма като
          баджа „−X%" по редовете */}
      <span
        className={`inline-flex shrink-0 items-center rounded-lg bg-pos-disc-bg font-bold leading-none text-pos-disc-ink ${
          compact ? "h-6 px-2 text-[12px]" : "h-7 px-2.5 text-sm"
        }`}
      >
        {formatSignedBillPercent(percent)}
      </span>
      <div className="min-w-0 flex-1">
        <div
          className={`truncate font-extrabold leading-tight text-pos-ink ${
            compact ? "text-[13px]" : "text-[15px]"
          }`}
        >
          {applied.client_name}
        </div>
        <div
          className={`truncate font-medium leading-tight text-pos-soft2 ${
            compact ? "text-[11px]" : "text-xs"
          }`}
        >
          {description}
        </div>
      </div>
      {percent !== 0 && (
        <div className="flex shrink-0 items-baseline gap-1.5">
          <span
            className={`font-medium text-pos-soft2 ${compact ? "text-[11px]" : "text-xs"}`}
          >
            {t(isSurcharge ? "clients.bannerSurcharged" : "clients.bannerSaved")}
          </span>
          <span
            className={`font-pos-mono font-bold text-pos-accent-ink [font-variant-numeric:tabular-nums] ${
              compact ? "text-sm" : "text-base"
            }`}
          >
            {amount.toFixed(2)}
          </span>
          <span
            className={`font-medium text-pos-muted ${compact ? "text-[10px]" : "text-[11px]"}`}
          >
            EUR
          </span>
        </div>
      )}
    </div>
  );
};
